<script setup>
import { useActions } from "vuex-composition-helpers";
import { computed } from "vue";
import { useHumanly } from "@humanlyio/interop/client";

const props = defineProps({
  mode: {
    type: String,
    default: "manual", // other option is "query"
  },

  recipients: {
    type: Array,
    default: () => [],
  },

  query: {
    type: Object,
    default: () => {},
  },

  size: {
    type: String,
    default: "normal",
  },
});

const {
  openSMSDialogWithRecipients,
  openSMSDialogWithQuery,
} = useActions([
  "openSMSDialogWithRecipients",
  "openSMSDialogWithQuery",
]);

const { permissions } = useHumanly();

const disabled = computed(() => {
  if (props.mode == "query") {
    return Object.keys(props.query).length === 0
      || Object.keys(props.query.query).length === 0
      || props.query.count === 0;
  }

  return props.recipients.length === 0 || props.recipients.every((recipient) => recipient.phoneOptOut);
});

const tooltip = computed(() => {
  if (disabled.value) {
    if (props.recipients.length != 1) {
      return "No valid candidates selected. Candidates must have a phone number and must have not opted out of receiving SMS messages.";
    } else if (props.recipients.every((recipient) => recipient.phoneOptOut)) {
      return "Candidate has opted out of receiving SMS messages."
    } else {
      return "Candidate does not have a phone number in our system."
    }
  }

  if ((props.mode == "manual" && props.recipients.length == 1)
    || props.mode == "query" && props.query.count === 1) {
    return "Send SMS to candidate";
  }

  if (props.mode == "query") {
    return `Send SMS to ${props.query.count} candidates`;
  }

  return `Send SMS to ${props.recipients.length} candidates`;
});

const hasSMSPermission = computed(() => {
  return permissions.value.includes("messaging:view");
});

function openSmsDialog() {
  if (props.mode == "query") {
    openSMSDialogWithQuery(props.query);
  } else {
    openSMSDialogWithRecipients(props.recipients);
  }
}
</script>

<template>
  <el-tooltip v-if="hasSMSPermission" :content="tooltip">
    <button class="compose-button" :class="{ 'compose-button--small': size === 'small' }" :disabled="disabled" @click="openSmsDialog">
      <span class="inner">
        <img src="@/assets/images/device-phone-mobile.svg" />
        <span v-if="size === 'normal'">SMS</span>
      </span>
    </button>
  </el-tooltip>
</template>

<style>
.compose-button {
  border-radius: 8px;
  background: #E5E9F9;
  border: 0;
  padding: 8px 24px;
  color: var(--Primaries-Primary-01, #0000C3);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  box-sizing: border-box;

  /* Button */
  font-family: "Work Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%;
  /* 25.6px */
}

.compose-button--small {
  padding: 0 16px;
  height: 32px;
}

.compose-button:disabled {
  opacity: 0.5;
}

.inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
</style>
